import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { logger } from '@/utils/logger'

interface AnnotationToolPanelProps {
  slideId: string
  detectedCells: any[] // Cells from AI detection
  selectedCellIndex: number | null
  onCellSelect: (index: number | null) => void
  onCellsDetected: (cells: any[]) => void  // Callback when cells are detected
  onEditModeToggle: (enabled: boolean) => void
  editMode: boolean
}

interface AnnotationStats {
  total: number
  verified: number
  used_in_training: number
  class_distribution: Record<string, number>
}

const CELL_CLASSES = [
  { value: 'normal_squamous', label: 'Normal Squamous', color: 'bg-green-100' },
  { value: 'normal_intermediate', label: 'Normal Intermediate', color: 'bg-green-50' },
  { value: 'normal_superficial', label: 'Normal Superficial', color: 'bg-green-50' },
  { value: 'asc_us', label: 'ASC-US', color: 'bg-yellow-100' },
  { value: 'asc_h', label: 'ASC-H', color: 'bg-orange-100' },
  { value: 'lsil', label: 'LSIL', color: 'bg-orange-200' },
  { value: 'hsil', label: 'HSIL', color: 'bg-red-200' },
  { value: 'scc', label: 'SCC', color: 'bg-red-300' },
  { value: 'agc', label: 'AGC', color: 'bg-purple-200' },
  { value: 'ais', label: 'AIS', color: 'bg-purple-300' },
  { value: 'adenocarcinoma', label: 'Adenocarcinoma', color: 'bg-red-400' },
  { value: 'inflammation', label: 'Inflammation', color: 'bg-blue-100' },
  { value: 'candida', label: 'Candida', color: 'bg-pink-100' },
  { value: 'trichomonas', label: 'Trichomonas', color: 'bg-teal-100' },
]

const QUALITY_LEVELS = [
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
]

export const AnnotationToolPanel: React.FC<AnnotationToolPanelProps> = ({
  slideId,
  detectedCells,
  selectedCellIndex,
  onCellSelect,
  onCellsDetected,
  onEditModeToggle,
  editMode
}) => {
  const [selectedClass, setSelectedClass] = useState('hsil')
  const [quality, setQuality] = useState('good')
  const [confidenceLevel, setConfidenceLevel] = useState(5)
  const [notes, setNotes] = useState('')
  const [stats, setStats] = useState<AnnotationStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [detectingCells, setDetectingCells] = useState(false)

  useEffect(() => {
    loadStats()
  }, [slideId])

  // Auto-trigger detection when panel opens
  useEffect(() => {
    if (detectedCells.length === 0 && !detectingCells) {
      handleAutoDetection()
    }
  }, [])

  const handleAutoDetection = async () => {
    setDetectingCells(true)
    toast.loading('Detecting all cells...', { id: 'detecting' })

    try {
      const token = localStorage.getItem('token')

      // Start detection task using new comprehensive cell detector
      const startResponse = await axios.post(
        '/api/v1/ai/cell-detector/detect/',
        {
          slide_id: slideId,
          method: 'watershed'  // Options: watershed, adaptive, blob
        },
        { headers: { Authorization: `Token ${token}` } }
      )

      const taskId = startResponse.data.task_id
      logger.debug('Cell detection task started:', taskId)

      // Poll for completion
      const pollStatus = async () => {
        try {
          const statusResponse = await axios.get(
            `/api/v1/ai/cell-detector/status/${taskId}/`,
            { headers: { Authorization: `Token ${token}` } }
          )

          const status = statusResponse.data.status
          const progress = statusResponse.data.progress || 0

          logger.debug('Detection status:', status, 'progress:', progress)

          if (progress > 0) {
            toast.loading(`Detecting cells... ${progress}%`, { id: 'detecting' })
          }

          if (status === 'completed') {
            const result = statusResponse.data.result
            logger.debug('Detection result:', result)

            const timeStr = result.processing_time ? `in ${result.processing_time.toFixed(1)}s` : ''
            toast.success(`Detected ${result.total_cells || 0} cells ${timeStr}!`, { id: 'detecting' })

            // Update parent with detected cells
            if (result.cells && result.cells.length > 0) {
              onCellsDetected(result.cells)
            }

            setDetectingCells(false)
            return
          } else if (status === 'failed') {
            toast.error('Cell detection failed: ' + statusResponse.data.error, { id: 'detecting' })
            setDetectingCells(false)
            return
          }

          // Still processing, poll again
          setTimeout(pollStatus, 2000)
        } catch (error) {
          console.error('Polling error:', error)
          setTimeout(pollStatus, 2000)  // Retry
        }
      }

      // Start polling after 2 seconds
      setTimeout(pollStatus, 2000)

    } catch (error: any) {
      console.error('Detection error:', error)
      toast.error(error.response?.data?.error || 'Failed to start cell detection', { id: 'detecting' })
      setDetectingCells(false)
    }
  }

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(
        `/api/v1/ai/annotations/statistics/?slide_id=${slideId}`,
        { headers: { Authorization: `Token ${token}` } }
      )
      setStats(response.data)
    } catch (err) {
      console.error('Failed to load stats:', err)
    }
  }

  const handleSaveAnnotation = async () => {
    if (selectedCellIndex === null) {
      toast.error('Please select a cell first')
      return
    }

    const cell = detectedCells[selectedCellIndex]
    if (!cell) {
      toast.error('Cell not found')
      return
    }

    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const annotationData = {
        slide: slideId,
        cell_class: selectedClass,
        bbox_x: Math.round(cell.bbox.x),
        bbox_y: Math.round(cell.bbox.y),
        bbox_width: Math.round(cell.bbox.width),
        bbox_height: Math.round(cell.bbox.height),
        quality,
        confidence_level: confidenceLevel,
        notes
      }

      await axios.post(
        `/api/v1/ai/annotations/`,
        annotationData,
        { headers: { Authorization: `Token ${token}` } }
      )

      toast.success('Annotation saved!')
      setNotes('')
      loadStats()
      onCellSelect(null) // Deselect cell after saving
    } catch (err: any) {
      console.error('Failed to save annotation:', err)
      toast.error(err.response?.data?.detail || 'Failed to save annotation')
    } finally {
      setLoading(false)
    }
  }

  const handleExportForTraining = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(
        `/api/v1/ai/annotations/export_for_training/`,
        { slide_id: slideId },
        {
          headers: { Authorization: `Token ${token}` },
          responseType: 'blob'
        }
      )

      // Download the file
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `annotations_${slideId}.json`)
      document.body.appendChild(link)
      link.click()
      link.remove()

      toast.success('Annotations exported!')
    } catch (err) {
      console.error('Failed to export:', err)
      toast.error('Failed to export annotations')
    }
  }

  const selectedCell = selectedCellIndex !== null ? detectedCells[selectedCellIndex] : null

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 max-w-md">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-800">Annotation Tool</h3>
        <button
          onClick={() => onCellSelect(null)}
          className="text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>
      </div>

      {/* Detection Status */}
      <div className="mb-4 p-3 bg-blue-50 rounded">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-blue-900">
            Detected Cells: {detectedCells.length}
          </span>
          <button
            onClick={handleAutoDetection}
            disabled={detectingCells}
            className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {detectingCells ? 'Detecting...' : 'Re-detect'}
          </button>
        </div>
        {detectedCells.length === 0 && !detectingCells && (
          <p className="text-xs text-blue-700">
            Click Re-detect to find cells in this slide
          </p>
        )}
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-gray-50 p-2 rounded text-center">
          <div className="text-2xl font-bold text-gray-800">{stats?.total || 0}</div>
          <div className="text-xs text-gray-600">Total</div>
        </div>
        <div className="bg-green-50 p-2 rounded text-center">
          <div className="text-2xl font-bold text-green-700">{stats?.verified || 0}</div>
          <div className="text-xs text-gray-600">Verified</div>
        </div>
        <div className="bg-blue-50 p-2 rounded text-center">
          <div className="text-2xl font-bold text-blue-700">{stats?.used_in_training || 0}</div>
          <div className="text-xs text-gray-600">In Training</div>
        </div>
      </div>

      {/* Cell Selection Info */}
      {selectedCell ? (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <div className="text-sm font-semibold text-yellow-900 mb-1">
            Selected Cell #{selectedCellIndex! + 1}
          </div>
          <div className="text-xs text-yellow-700">
            Position: ({Math.round(selectedCell.bbox.x)}, {Math.round(selectedCell.bbox.y)})
            <br />
            Size: {Math.round(selectedCell.bbox.width)} × {Math.round(selectedCell.bbox.height)}px
          </div>
        </div>
      ) : (
        <div className="mb-4 p-3 bg-gray-50 rounded text-center text-sm text-gray-600">
          Click on a cell in the image to annotate it
        </div>
      )}

      {/* Cell Classification Form */}
      <div className="space-y-3">
        {/* Cell Class */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Cell Class:
          </label>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={!selectedCell}
          >
            {CELL_CLASSES.map((cls) => (
              <option key={cls.value} value={cls.value}>
                {cls.label}
              </option>
            ))}
          </select>
        </div>

        {/* Quality */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Quality:
          </label>
          <select
            value={quality}
            onChange={(e) => setQuality(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={!selectedCell}
          >
            {QUALITY_LEVELS.map((q) => (
              <option key={q.value} value={q.value}>
                {q.label}
              </option>
            ))}
          </select>
        </div>

        {/* Confidence Level */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Confidence Level: {confidenceLevel}/5
          </label>
          <input
            type="range"
            min="1"
            max="5"
            value={confidenceLevel}
            onChange={(e) => setConfidenceLevel(parseInt(e.target.value))}
            className="w-full"
            disabled={!selectedCell}
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes (optional):
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional notes..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={2}
            disabled={!selectedCell}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => onEditModeToggle(!editMode)}
            disabled={!selectedCell}
            className={`flex-1 py-2 px-4 rounded font-semibold transition-colors ${
              editMode
                ? 'bg-orange-600 text-white hover:bg-orange-700'
                : 'bg-gray-600 text-white hover:bg-gray-700'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {editMode ? '✓ Editing' : '✏️ Edit Box'}
          </button>
          <button
            onClick={handleSaveAnnotation}
            disabled={!selectedCell || loading}
            className="flex-1 bg-green-600 text-white py-2 px-4 rounded font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Saving...' : '💾 Save'}
          </button>
        </div>
      </div>

      {/* Export Button */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <button
          onClick={handleExportForTraining}
          className="w-full bg-purple-600 text-white py-2 px-4 rounded font-semibold hover:bg-purple-700 transition-colors"
        >
          📦 Export for AI Training
        </button>
      </div>

      {/* Instructions */}
      <div className="mt-4 p-3 bg-gray-50 rounded text-xs text-gray-600">
        <div className="font-semibold mb-1">How to annotate:</div>
        <ol className="list-decimal list-inside space-y-1">
          <li>Cells are auto-detected when tool opens</li>
          <li>Click on a cell to select it</li>
          <li>Choose cell class and quality</li>
          <li>Click "Edit Box" to redraw if needed</li>
          <li>Click "Save" to store annotation</li>
          <li>Click "Export" when finished</li>
        </ol>
      </div>
    </div>
  )
}
